<template>
  <div class="pageRoot linkListPage collectionModule">
    <div ref="pageCard" class="pageShell">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <data-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  row-key="id"
                >
                  <el-table-column
                    label="数据集名称"
                    prop="name"
                    align="center"
                    header-align="center"
                    min-width="180"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="nameCell">{{ scope.row.name || '—' }}</span>
                    </template>
                  </el-table-column>

                  <el-table-column
                    label="关联应用"
                    align="center"
                    header-align="center"
                    min-width="160"
                  >
                    <template slot-scope="scope">
                      <div class="appRefCell">
                        <img
                          v-if="scope.row.appIcon"
                          v-lazy="scope.row.appIcon"
                          alt=""
                          class="appRefCell__icon"
                        />
                        <span class="appRefCell__name">{{ scope.row.appName || '—' }}</span>
                      </div>
                    </template>
                  </el-table-column>

                  <el-table-column
                    label="过滤条件"
                    align="center"
                    header-align="center"
                    min-width="280"
                  >
                    <template slot-scope="scope">
                      <div class="filterSummaryCell">
                        <div class="filterSummaryCell__row">
                          <span class="filterSummaryCell__label">国家</span>
                          <span
                            class="filterSummaryCell__value"
                            :class="{
                              'filterSummaryCell__value--muted':
                                isDefaultFilterValue(scope.row.country),
                            }"
                            :title="formatCountryDisplay(scope.row.country)"
                          >
                            {{ formatCountryDisplay(scope.row.country) }}
                          </span>
                        </div>
                        <div class="filterSummaryCell__row">
                          <span class="filterSummaryCell__label">应用版本</span>
                          <span
                            class="filterSummaryCell__value"
                            :class="{
                              'filterSummaryCell__value--muted':
                                isDefaultFilterValue(scope.row.appVersion),
                            }"
                            :title="formatVersionFilterDisplay(scope.row.appVersion)"
                          >
                            {{ formatVersionFilterDisplay(scope.row.appVersion) }}
                          </span>
                        </div>
                        <div class="filterSummaryCell__row">
                          <span class="filterSummaryCell__label">系统版本</span>
                          <span
                            class="filterSummaryCell__value"
                            :class="{
                              'filterSummaryCell__value--muted':
                                isDefaultFilterValue(scope.row.apiVersion),
                            }"
                            :title="formatVersionFilterDisplay(scope.row.apiVersion)"
                          >
                            {{ formatVersionFilterDisplay(scope.row.apiVersion) }}
                          </span>
                        </div>
                      </div>
                    </template>
                  </el-table-column>

                  <el-table-column
                    label="状态"
                    prop="status"
                    align="center"
                    header-align="center"
                    width="110"
                  >
                    <template slot-scope="scope">
                      <span
                        class="statusPill"
                        :class="'statusPill--' + getDatasetStatusPillVariant(scope.row.status)"
                      >
                        <span class="statusPill__dot" aria-hidden="true" />
                        {{ getDatasetStatusLabel(scope.row.status) }}
                      </span>
                    </template>
                  </el-table-column>

                  <el-table-column
                    label="更新时间"
                    prop="updateTime"
                    align="center"
                    header-align="center"
                    min-width="160"
                    show-overflow-tooltip
                  />

                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    width="200"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-view"
                          @click.stop="lookInfo(scope.row.id)"
                        >
                          查看
                        </el-button>
                        <el-button
                          v-if="showActions"
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-edit"
                          v-check-role
                          @click.stop="openEdit(scope.row.id)"
                        >
                          编辑
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-coin" /></div>
                <p class="tableEmpty__title">暂无数据集</p>
                <p class="tableEmpty__desc">调整筛选条件或新增数据集。</p>
                <el-button
                  type="primary"
                  size="small"
                  v-check-role
                  @click="$router.push('/channel/data/create')"
                >
                  新增数据集
                </el-button>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button
                  type="button"
                  class="filterDrawerPanel__closeBtn"
                  @click="closeFilterDrawer"
                >
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <data-list-filter-panel
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[25, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getDataList, getaAllRelationApp } from '../../../api/dataManage'
import DataListFilterPanel from './DataListFilterPanel.vue'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'
import {
  findAppOptionLabel,
  formatCountryDisplay,
  formatVersionFilterDisplay,
  getDatasetStatusLabel,
  getDatasetStatusPillVariant,
  isDefaultFilterValue,
} from './dataListHelpers'

export default {
  name: 'DataManageList',
  components: {
    DataListFilterPanel,
    ControlBarDisplay,
    ControlBarNav,
  },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      dataListName: '',
      relationApp: '',
      status: '',
      appOptions: [],
      tableData: [],
      currentPage: 1,
      currentPageSize: 25,
      total: 0,
      loading: false,
    }
  },
  computed: {
    showActions() {
      return this.$route.meta.role == 2
    },
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.dataListName, this.relationApp, this.status].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    activeScopeFilters() {
      const chips = []
      if (String(this.dataListName || '').trim()) {
        chips.push({
          key: 'dataListName',
          label: '数据集名称',
          value: this.dataListName.trim(),
        })
      }
      if (this.relationApp !== '' && this.relationApp != null) {
        chips.push({
          key: 'relationApp',
          label: '关联应用',
          value: findAppOptionLabel(this.appOptions, this.relationApp),
        })
      }
      if (String(this.status || '').trim()) {
        chips.push({
          key: 'status',
          label: '状态',
          value: getDatasetStatusLabel(this.status),
        })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(' · ')
    },
    filterPanelProps() {
      return {
        dataListName: this.dataListName,
        relationApp: this.relationApp,
        status: this.status,
        appOptions: this.appOptions,
        activeFilterCount: this.activeFilterCount,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'go-create': () => this.$router.push('/channel/data/create'),
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:dataListName': (value) => {
          this.dataListName = value
        },
        'update:relationApp': (value) => {
          this.relationApp = value
        },
        'update:status': (value) => {
          this.status = value
        },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.loadAppOptions()
    this.getList()
  },
  methods: {
    formatCountryDisplay,
    formatVersionFilterDisplay,
    getDatasetStatusLabel,
    getDatasetStatusPillVariant,
    isDefaultFilterValue,
    loadAppOptions() {
      getaAllRelationApp().then((res) => {
        if (res.success) {
          this.appOptions = res.data || []
        }
      })
    },
    getList() {
      this.loading = true
      getDataList({
        currentPage: this.currentPage,
        currentPageSize: this.currentPageSize,
        dataListName: this.dataListName,
        relationApp: this.relationApp,
        status: this.status,
      })
        .then((res) => {
          if (res.success) {
            this.total = res.total
            this.tableData = res.data || []
          }
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.measureTableZoneWidth()
            this.scheduleTableLayout()
          })
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.dataListName = ''
      this.relationApp = ''
      this.status = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getList()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    lookInfo(id) {
      this.$router.push(`/channel/data/info/${id}`)
    },
    openEdit(id) {
      this.$router.push(`/channel/data/edit/${id}`)
    },
  },
}
</script>

<style scoped>
.appRefCell {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  max-width: 100%;
}

.appRefCell__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: var(--link-shadow-card, 0 2px 8px rgba(28, 25, 23, 0.08));
}

.appRefCell__name {
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filterSummaryCell {
  display: inline-grid;
  grid-template-columns: 76px max-content;
  gap: 6px 14px;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  padding: 4px 0;
  text-align: center;
}

.filterSummaryCell__row {
  display: contents;
}

.filterSummaryCell__label {
  justify-self: end;
  font-size: var(--link-text-xs);
  color: var(--link-ink-muted);
  line-height: 1.45;
  white-space: nowrap;
}

.filterSummaryCell__value {
  justify-self: start;
  max-width: 220px;
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
  line-height: 1.45;
  word-break: break-word;
  text-align: left;
}

.filterSummaryCell__value--muted {
  color: var(--link-ink-muted);
}
</style>
